import {useState, useEffect, useRef} from 'react'
import {Link} from "react-router-dom"
import CustomAvatar from '@src/views/components/CustomAvatar'
import {ArchiveIcon, UnarchiveIcon} from '@src/views/components/ArchiveIcons'
import {useDispatch, useSelector} from 'react-redux'
import {
    selectChatFromServer,
    selectedContactDetail,
    changeReduxState,
    archiveConversation,
    unarchiveConversation,
    getChatContactsFromServer
} from './store/actions'
import {NULL, RD} from './store/actionTypes'
import classnames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {X, Search, Archive, Inbox, Send} from 'react-feather'
import {InputGroup, InputGroupAddon, Input, InputGroupText, Badge, Spinner, Nav, NavItem, NavLink} from 'reactstrap'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {PropTypes} from "prop-types"

const SidebarLeft = props => {
    const {store, sidebar, handleSidebar, userSidebarLeft, handleUserSidebarLeft, chatRdContacts, selectedUserID, activeTab, onTabChange} = props
    const {chats, userProfile, contacts} = store
    const dispatch = useDispatch()
    const chatRdStoreSlice = useSelector(state => state.chatRd)

    const sweetAlert = withReactContent(Swal)

    const [query, setQuery] = useState('')
    const [active, setActive] = useState({})
    const [filteredContacts, setFilteredContacts] = useState([])
    const [contactsOfRDs, setContactsOfRDs] = useState([])
    const [contactsOfRDsForSearching, setContactsOfRDsForSearching] = useState([])
    const [loader, setLoader] = useState(true)

    const chatOnLoad = useRef(true)

    // Compute unread message count for Inbox vs Archive tab
    const inboxUnread = (store?.unreadCounts?.inbox !== undefined)
        ? store.unreadCounts.inbox
        : (store?.inboxContacts?.data || []).reduce((sum, c) => sum + (c.unread_messages || 0), 0)

    const archiveUnread = (store?.unreadCounts?.archive !== undefined)
        ? store.unreadCounts.archive
        : (store?.archiveContacts?.data || []).reduce((sum, c) => sum + (c.unread_messages || 0), 0)

    const TABS = [
        {
            key: 'inbox',
            label: 'Inbox',
            icon: <Inbox size={14}/>,
            badge: inboxUnread > 0 ? inboxUnread : null
        },
        {key: 'sent', label: 'Sent', icon: <Send size={14}/>},
        {
            key: 'archive',
            label: 'Archive',
            icon: <Archive size={14}/>,
            badge: archiveUnread > 0 ? archiveUnread : null
        }
    ]

    const handleUserClick = (type, contact) => {
        dispatch(changeReduxState(NULL.LOAD_SELECTED_RD_USER_CHAT, true))
        dispatch(selectChatFromServer(contact.chat_user))
        dispatch(selectedContactDetail(contact))
        setActive({type, id: contact.chat_user})
        if (sidebar === true) {
            handleSidebar()
        }
        dispatch({type: 'CLEAR_RD_CHAT_COUNT_ON_CLICK', payload: contact})
    }

    const handleArchive = (e, chatUser) => {
        e.stopPropagation()
        sweetAlert.fire({
            title: 'Archive this thread?',
            text: 'It will be moved to your Archive tab.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel'
        }).then(result => {
            if (result.isConfirmed) {
                dispatch(archiveConversation(chatUser))
                const isCurrentlyOpened = active?.id === chatUser || parseInt(selectedUserID) === chatUser
                if (isCurrentlyOpened && onTabChange) {
                    onTabChange('archive')
                }
            }
        })
    }

    const handleUnarchive = (e, chatUser) => {
        e.stopPropagation()
        sweetAlert.fire({
            title: 'Unarchive this thread?',
            text: 'It will be moved back to your Inbox.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Unarchive',
            cancelButtonText: 'Cancel'
        }).then(result => {
            if (result.isConfirmed) {
                dispatch(unarchiveConversation(chatUser))
            }
        })
    }

    // Helper: get latest timestamp for sorting
    const getLatestTimestamp = (item) => {
        const raw = item?.updated_at || item?.last_message_date || item?.last_message_time || item?.created_at
        if (!raw) return 0
        const d = new Date(raw)
        return isNaN(d.getTime()) ? 0 : d.getTime()
    }

    useEffect(() => {
        if (!!chatRdContacts && !!selectedUserID && chatOnLoad.current) {
            chatOnLoad.current = false
            const contact = (chatRdContacts.data || []).find(c => c.chat_user === parseInt(selectedUserID))
            setActive(null)
            if (contact) {
                handleUserClick('contact', contact)
            }
        }

        if (!!chatRdContacts) {
            // Deduplicate contacts by chat_user / user_id
            const uniqueMap = new Map()
            ;(chatRdContacts.data || []).forEach(c => {
                const key = c.chat_user || c.user_id
                if (key != null && !uniqueMap.has(String(key))) {
                    uniqueMap.set(String(key), c)
                }
            })
            const uniqueContacts = Array.from(uniqueMap.values())
            const sorted = uniqueContacts.sort(
                (a, b) => getLatestTimestamp(b) - getLatestTimestamp(a)
            )
            setContactsOfRDs(sorted)
            setContactsOfRDsForSearching(sorted)
            setLoader(false)
        }
    }, [chatRdContacts, activeTab])

    useEffect(() => {
        const error = chatRdStoreSlice?.rdContactLoadError?.response?.status
        if (error && error !== 401) {
            sweetAlert.fire({
                title: 'Connection Error!',
                icon: 'error',
                html: '<p>Please reload the page.</p>',
                confirmButtonText: 'Reload',
                allowOutsideClick: false,
                showCancelButton: true
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.reload()
                }
            })
            dispatch({type: "RD_CONTACT_LOAD_ERROR_RESET"})
            setLoader(false)
        }
    }, [chatRdStoreSlice])

    // Helper: format sidebar timestamp (e.g. 10:48 AM, 18 Sept, or Sept 24)
    const formatSidebarTime = (item) => {
        const rawTime = item?.last_message_created_at || item?.created_at || item?.last_message_timezone || item?.timezone || item?.last_message_time || item?.updated_at
        if (!rawTime) return ''
        const d = new Date(rawTime)
        if (isNaN(d.getTime())) return rawTime

        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()

        if (isToday) {
            return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        }

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec']
        const day = d.getDate()
        const month = monthNames[d.getMonth()]
        const year = String(d.getFullYear()).slice(-2)

        if (d.getFullYear() === now.getFullYear()) {
            return `${day} ${month}`
        }
        return `${month} ${year}`
    }

    const userData = JSON.parse(localStorage.getItem(`${process.env.REACT_APP_USERDATA}`)) || {}
    const currentUserId = userData?.chat_id || userData?.id || userProfile?.id

    // Helper: determine if a brand is inactive / no longer available
    const isBrandInactive = (item) => {
        if (!item) return false
        if (item.brand_is_paid === false || item.brand_is_paid === 0 || item.brand_is_paid === '0' || item.brand_is_paid === 'false') {
            return true
        }
        if (item.is_paid === false || item.is_paid === 0 || item.is_paid === '0' || item.is_paid === 'false') {
            return true
        }
        if (item.is_active === false || item.is_active === 0 || item.is_active === '0' || item.is_active === 'false') {
            return true
        }
        if (item.brand_is_active === false || item.brand_is_active === 0 || item.brand_is_active === '0' || item.brand_is_active === 'false') {
            return true
        }
        return false
    }

    // Helper: determine if thread is waiting for reply (shows only after 7 days of no reply, and not archived)
    const isWaitingForReply = (item) => {
        if (activeTab === 'archive' || item?.is_archived === true || item?.is_archived === 1 || item?.is_archived === '1' || item?.is_auto_archived === true || isBrandInactive(item)) {
            return false
        }
        if (item?.is_mass_group || item?.user_type === 'mass') {
            return false
        }
        // 1. Must be sent by me
        const lastMsgFromId = item?.last_message_from_user_id
        const sentByMe = (lastMsgFromId && currentUserId && parseInt(lastMsgFromId) === parseInt(currentUserId)) ||
            (item?.last_message && (item.last_message.startsWith('You:') || item.last_message.startsWith('You -')))

        if (!sentByMe) return false

        // 2. Must be unreplied
        const isUnreplied = item?.is_replied === 0 || item?.is_replied === '0' || item?.is_replied === undefined

        if (!isUnreplied) return false

        // 3. Must be after 7 days
        if (typeof item?.is_waiting === 'boolean') {
            return item.is_waiting
        }

        const rawDate = item?.last_message_created_at || item?.created_at || item?.last_message_time
        if (!rawDate) return false

        const sentAt = new Date(rawDate)
        if (isNaN(sentAt.getTime())) return false

        const now = new Date()
        const diffMs = now - sentAt
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        return diffDays >= 7
    }

    const renderContacts = () => {
        const result = contactsOfRDs.filter(e => e.length !== 0)
        return result?.map(item => {
            const isActive = active?.type === 'contact' && active?.id === item?.chat_user
            const hasUnread = item?.unread_messages > 0
            const isWaiting = isWaitingForReply(item)
            const lastMsgFromId = item?.last_message_from_user_id
            const isSentByMe = (lastMsgFromId && currentUserId && parseInt(lastMsgFromId) === parseInt(currentUserId)) ||
                (item?.last_message && (item.last_message.startsWith('You:') || item.last_message.startsWith('You -')))

            return (
                <li className={classnames({active: isActive})}
                    key={item?.chat_user}
                    style={{position: 'relative'}}>
                    <button className='btn btn-link p-0 d-flex align-items-center w-100 text-left'
                            onClick={() => handleUserClick('contact', item)}>
                        <div style={{position: 'relative', flexShrink: 0}}>
                            <Link to={`/dashboard/rd/brands/${item?.brand_id}`} title="Go to profile"
                                  onClick={(e) => e.stopPropagation()}>
                                <CustomAvatar
                                    name={item?.brand_name || item?.name}
                                    img={item.brand_logo || item.company_logo}
                                    imgHeight='42'
                                    imgWidth='42'
                                    className="chat-img-animation avatar-img-fit-contain"
                                />
                            </Link>
                        </div>
                        <div className='chat-info flex-grow-1 ml-75 overflow-hidden'>
                            {/* Top Line: Brand Name (truncates with ...) + Timestamp & Unread Blue Dot */}
                            <div className='d-flex align-items-center justify-content-between w-100' style={{ gap: '10px' }}>
                                <h5
                                    className={classnames('mb-0 text-truncate font-weight-bold', {
                                        'avatar-brands-unpaid': item?.brand_is_paid === false && !isActive
                                    })}
                                    title={item?.brand_name}
                                    style={{ flexShrink: 1, minWidth: 0, fontSize: '13.5px', lineHeight: '1.2' }}
                                >
                                    {item?.brand_name}
                                </h5>
                                <div className='d-flex align-items-center flex-shrink-0' style={{ gap: '4px' }}>
                                    <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                        {formatSidebarTime(item)}
                                    </span>
                                    {hasUnread && (
                                        <span className='chat-unread-blue-dot' title={`${item.unread_messages} unread messages`} />
                                    )}
                                </div>
                            </div>

                            {/* Bottom Line: Last Message Snippet + Waiting Badge */}
                            <div className='d-flex align-items-center justify-content-between w-100 mt-25' style={{ gap: '8px' }}>
                                <span className="small text-muted text-truncate flex-grow-1" style={{ lineHeight: '1.3' }}>
                                    {isSentByMe && !item?.last_message?.startsWith('You') ? `You: ${item?.last_message}` : item?.last_message}
                                </span>
                                {item?.has_feedback ? (
                                    <span className='chat-feedback-badge flex-shrink-0' title='Sample Feedback Requested'>
                                        Feedback
                                    </span>
                                ) : isWaiting ? (
                                    <span className='chat-waiting-badge flex-shrink-0'>
                                        Waiting
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </button>
                </li>
            )
        })
    }

    const handleFilter = e => {
        setQuery(e.target.value)
        setContactsOfRDs(contactsOfRDsForSearching.filter(contact => contact?.brand_name?.toLowerCase().includes(e.target.value.toLowerCase())))
    }

    return store ? (
        <div className='sidebar-left'>
            <div className='sidebar'>
                <div className={classnames('sidebar-content', {show: sidebar === true})}>
                    <div className='sidebar-close-icon'>
                        <button className='btn btn-link p-0 bg-danger rounded-circle text-white'
                                onClick={handleSidebar}>
                            <X/>
                        </button>
                    </div>

                    {/* Search bar */}
                    <div className='chat-fixed-search'>
                        <div className='d-flex align-items-center w-100'>
                            <InputGroup className='input-group-merge w-100'>
                                <InputGroupAddon addonType='prepend'>
                                    <InputGroupText className='round'>
                                        <Search className='text-muted' size={14}/>
                                    </InputGroupText>
                                </InputGroupAddon>
                                <Input
                                    value={query}
                                    className='round'
                                    placeholder='Search messages...'
                                    onChange={handleFilter}
                                />
                            </InputGroup>
                        </div>
                    </div>

                    {/* Pill Tabs — Inbox, Sent, Archive (Matching Mockup Design) */}
                    <div className='chat-tabs-pills-wrapper d-flex align-items-center py-75'>
                        <div className='d-flex align-items-center w-100' style={{ gap: '6px' }}>
                            {TABS.map(tab => {
                                const isActive = activeTab === tab.key
                                return (
                                    <button
                                        key={tab.key}
                                        type='button'
                                        className={classnames('btn chat-pill-tab-btn flex-fill d-flex align-items-center justify-content-center', {
                                            'active-pill': isActive,
                                            'inactive-pill': !isActive
                                        })}
                                        onClick={() => {
                                            if (activeTab !== tab.key) {
                                                setLoader(true)
                                                onTabChange(tab.key)
                                            }
                                        }}
                                    >
                                        <span>{tab.label}</span>
                                        {Number(tab.badge) > 0 && (
                                            <span className='chat-pill-badge'>
                                                {tab.badge}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                            {loader && (
                                <span className='ml-25 flex-shrink-0'>
                                    <Spinner color='primary' style={{ height: '14px', width: '14px' }} />
                                </span>
                            )}
                        </div>
                    </div>

                    <PerfectScrollbar className='chat-user-list-wrapper list-group' options={{wheelPropagation: false}}>
                        {loader ? (
                            <div className='py-1'>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className='skeleton-item'>
                                        <div className='skeleton-avatar'/>
                                        <div className='skeleton-info'>
                                            <div className='skeleton-line title'/>
                                            <div className='skeleton-line subtitle'/>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <ul className='chat-users-list contact-list media-list pb-4'>
                                {contactsOfRDs?.length > 0 ? (
                                    renderContacts()
                                ) : (
                                    <li className='text-center py-3 text-muted border-top-0'
                                        style={{fontSize: '0.85rem', listStyle: 'none'}}>
                                        No conversations found
                                    </li>
                                )}
                            </ul>
                        )}
                    </PerfectScrollbar>
                </div>
            </div>
        </div>
    ) : null
}

SidebarLeft.propTypes = {
    store: PropTypes.shape({
        userProfile: PropTypes.object,
        contacts: PropTypes.array
    }),

    sidebar: PropTypes.bool,

    handleSidebar: PropTypes.func,

    userSidebarLeft: PropTypes.bool,

    handleUserSidebarLeft: PropTypes.func,

    chatRdContacts: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object)
    }),

    selectedUserID: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),

    activeTab: PropTypes.string,

    onTabChange: PropTypes.func
}

export default SidebarLeft